import Image, { ImageProps } from 'next/image';
import { CSSProperties, RefObject } from 'react';

// Additional imgix render api types can be found here
// https://github.com/prismicio-community/imgix-url-builder/blob/main/src/types.generated.ts
type ImgixProps = {
  fit?:
    | 'clip'
    | 'clamp'
    | 'crop'
    | 'facearea'
    | 'fill'
    | 'fillmax'
    | 'max'
    | 'min'
    | 'scale';
  crop?:
    | 'top'
    | 'bottom'
    | 'left'
    | 'right'
    | 'faces'
    | 'entropy'
    | 'edges'
    | 'focalpoint';
};
type PrismicImageProps = Omit<ImageProps, 'src'> &
  ImgixProps & {
    src?: string;
    layout?: 'fill' | 'fixed' | 'intrinsic' | 'responsive';
    lazyRoot?: RefObject<HTMLDivElement> | null;
    objectFit?: CSSProperties['objectFit'];
    objectPosition?: CSSProperties['objectPosition'];
    onLoadingComplete?: () => void;
  };

const PrismicImage = ({
  src,
  fit,
  crop,
  width,
  height,
  alt = '',
  fill,
  style: callerStyle,
  layout: _layout,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  lazyRoot: _lazyRoot,
  objectFit: _objectFit,
  objectPosition: _objectPosition,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  onLoadingComplete: _onLoadingComplete,
  ...additionalProps
}: PrismicImageProps) => {
  if (!src) return null;

  // Translate legacy layout="fill" to the new fill prop
  const isFill = fill || _layout === 'fill';

  const imgixSrc = new URL(src);
  // Set Quality to 100 so vercel next image can handle compression
  imgixSrc.searchParams.append('quality', String(100));

  // Apply Imgix Rendering APIs (skip w/h in fill mode — no explicit dimensions)
  if (width && !isFill) {
    imgixSrc.searchParams.append('w', String(width));
  }
  if (height && !isFill) {
    imgixSrc.searchParams.append('h', String(height));
  }
  if (fit) {
    imgixSrc.searchParams.append('fit', fit);
  }
  if (crop) {
    imgixSrc.searchParams.append('crop', crop);
  }

  // Translate legacy objectFit/objectPosition props to style
  const legacyStyle: CSSProperties = {
    objectFit: _objectFit,
    objectPosition: _objectPosition,
  };

  return (
    <Image
      src={imgixSrc.toString()}
      alt={alt}
      {...additionalProps}
      {...(isFill
        ? { fill: true, style: { ...legacyStyle, ...callerStyle } }
        : {
            width,
            height,
            style: {
              maxWidth: '100%',
              height: 'auto',
              ...legacyStyle,
              ...callerStyle,
            },
          })}
    />
  );
};

export default PrismicImage;
